import {
  Calendar,
  Clock,
  IndianRupee,
  Package as PackageIcon,
  ShoppingCart,
  Star,
  User,
  X,
} from 'lucide-react';
import { formatDateTime } from 'utils/dateFormatter';

export const ViewPackageModal = (props) => {
  const { isViewModalOpen, setIsViewModalOpen, package: pkg } = props;
  if (!isViewModalOpen || !pkg) return null;

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
      <div className='bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto custom-scrollbar'>
        {/* Header */}
        <div className='sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between'>
          <div className='flex items-center space-x-3'>
            <div className='p-2 bg-primary/10 rounded-lg'>
              <PackageIcon size={20} className='text-primary' />
            </div>
            <h2 className='text-lg font-semibold'>Package Details</h2>
          </div>
          <button
            onClick={() => setIsViewModalOpen(false)}
            className='inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
            aria-label='Close package details modal'
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className='p-6 space-y-6'>
          {/* Images Section */}
          {pkg.images && pkg.images.length > 0 && (
            <div className='space-y-2'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Package Images
              </label>
              <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3'>
                {pkg.images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Package image ${index + 1}`}
                    className='w-full h-32 object-cover rounded-lg border border-border'
                  />
                ))}
              </div>
            </div>
          )}

          {/* Videos Section */}
          {pkg.videos && pkg.videos.length > 0 && (
            <div className='space-y-2'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Package Videos
              </label>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                {pkg.videos.map((video, index) => (
                  <video
                    key={index}
                    src={video}
                    controls
                    className='w-full h-48 rounded-lg border border-border'
                  />
                ))}
              </div>
            </div>
          )}

          {/* Basic Info */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                <PackageIcon size={14} />
                <span>Package Name</span>
              </label>
              <p className='text-sm font-medium'>{pkg.name || 'N/A'}</p>
            </div>

            <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Slug
              </label>
              <p className='text-sm text-muted-foreground'>
                {pkg.slug || 'N/A'}
              </p>
            </div>
          </div>

          {/* Description */}
          {pkg.description && (
            <div className='space-y-2'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Description
              </label>
              <div className='bg-muted rounded-lg p-4 max-w-full'>
                <p className='text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere] max-w-full'>
                  {pkg.description}
                </p>
              </div>
            </div>
          )}

          {/* Seller Info */}
          <div className='bg-muted rounded-lg p-4 space-y-3'>
            <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
              <User size={14} />
              <span>Seller Information</span>
            </label>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
              <div>
                <p className='text-xs text-muted-foreground mb-1'>Name:</p>
                <p className='text-sm font-medium'>
                  {pkg.sellerId?.firstName || pkg.sellerId?.lastName
                    ? `${pkg.sellerId?.firstName || ''} ${pkg.sellerId?.lastName || ''}`.trim()
                    : 'N/A'}
                </p>
              </div>
              <div>
                <p className='text-xs text-muted-foreground mb-1'>Email:</p>
                <p className='text-sm'>{pkg.sellerId?.email || 'N/A'}</p>
              </div>
              <div>
                <p className='text-xs text-muted-foreground mb-1'>Phone:</p>
                <p className='text-sm'>{pkg.sellerId?.phoneNumber || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Services Included */}
          <div className='space-y-3'>
            <label className='text-xs font-medium text-muted-foreground uppercase'>
              Services Included
            </label>
            <div className='bg-blue-500/5 border border-blue-500/20 rounded-lg p-4'>
              <div className='flex items-center justify-between mb-2'>
                <span className='text-sm font-medium'>Total Services</span>
                <span className='px-3 py-1 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-full text-xs font-medium'>
                  {pkg.serviceIds?.length || 0} services
                </span>
              </div>
              {pkg.serviceIds && pkg.serviceIds.length > 0 && (
                <div className='space-y-2 mt-3'>
                  {pkg.serviceIds.map((service, index) => (
                    <div
                      key={service._id || index}
                      className='bg-card rounded-md p-3 border border-border'
                    >
                      <div className='flex items-center justify-between'>
                        <div className='flex-1'>
                          <p className='text-sm font-medium'>
                            {service.name || `Service ${index + 1}`}
                          </p>
                          {service.description && (
                            <p className='text-xs text-muted-foreground mt-1 whitespace-pre-wrap break-words [overflow-wrap:anywhere] max-w-full'>
                              {service.description}
                            </p>
                          )}
                        </div>
                        {service.pricing && (
                          <div className='text-right ml-3'>
                            <p className='text-sm font-medium'>
                              {service.pricing.currency || 'INR'}{' '}
                              {service.pricing.basePrice?.toFixed(2) || '0.00'}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Pricing Info */}
          <div className='bg-green-500/5 border border-green-500/20 rounded-lg p-4 space-y-3'>
            <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
              <IndianRupee size={14} />
              <span>Pricing Details</span>
            </label>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
              <div>
                <p className='text-xs text-muted-foreground mb-1'>
                  Accumulated Price:
                </p>
                <p className='text-sm font-medium'>
                  {pkg.pricing?.currency || 'INR'}{' '}
                  {pkg.pricing?.accumulatedPrice?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div>
                <p className='text-xs text-muted-foreground mb-1'>
                  Booking Price:
                </p>
                <p className='text-lg font-bold text-green-600 dark:text-green-400'>
                  {pkg.pricing?.currency || 'INR'}{' '}
                  {pkg.pricing?.bookingPrice?.toFixed(2) || '0.00'}
                </p>
              </div>
              {/* <div>
                <p className='text-xs text-muted-foreground mb-1'>Discount:</p>
                <p className='text-sm font-medium'>
                  {pkg.pricing?.currency || 'INR'}{' '}
                  {pkg.pricing?.discount?.toFixed(2) || '0.00'}
                </p>
              </div> */}
              {/* <div>
                <p className='text-xs text-muted-foreground mb-1'>
                  Discount %:
                </p>
                <p className='text-sm font-medium'>
                  {pkg.pricing?.discountPercentage > 0 ? (
                    <span className='px-2 py-1 bg-green-500/10 text-green-600 dark:text-green-400 rounded-full text-xs font-medium'>
                      {pkg.pricing?.discountPercentage}% OFF
                    </span>
                  ) : (
                    '0%'
                  )}
                </p>
              </div> */}
            </div>
          </div>

          {/* Duration */}
          {pkg.duration > 0 && (
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-1'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                  <Clock size={14} />
                  <span>Duration</span>
                </label>
                <p className='text-sm font-medium'>{pkg.duration}</p>
              </div>
              <div className='space-y-1'>
                <label className='text-xs font-medium text-muted-foreground uppercase'>
                  Duration Type
                </label>
                <p className='text-sm capitalize'>
                  {pkg.durationType || 'N/A'}
                </p>
              </div>
            </div>
          )}

          {/* Status Badges */}
          {/* <div className='space-y-2'>
            <label className='text-xs font-medium text-muted-foreground uppercase'>
              Status & Flags
            </label>
            <div className='flex flex-wrap gap-2'>
              <span
                className={`px-3 py-1 text-xs font-medium rounded-full ${
                  pkg.isActive
                    ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                    : 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
                }`}
              >
                {pkg.isActive ? 'Active' : 'Inactive'}
              </span>
              <span
                className={`px-3 py-1 text-xs font-medium rounded-full ${
                  pkg.isPopular
                    ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400'
                    : 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
                }`}
              >
                {pkg.isPopular ? 'Popular' : 'Regular'}
              </span>
              <span
                className={`px-3 py-1 text-xs font-medium rounded-full ${
                  pkg.isFeatured
                    ? 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
                    : 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
                }`}
              >
                {pkg.isFeatured ? 'Featured' : 'Normal'}
              </span>
            </div>
          </div> */}

          {/* Statistics */}
          {/* {pkg.stats && (
            <div className='bg-purple-500/5 border border-purple-500/20 rounded-lg p-4 space-y-3'>
              <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                <ShoppingCart size={14} />
                <span>Booking Statistics</span>
              </label>
              <div className='grid grid-cols-3 gap-4'>
                <div>
                  <p className='text-xs text-muted-foreground mb-1'>
                    Total Bookings:
                  </p>
                  <p className='text-lg font-bold'>
                    {pkg.stats.totalBookings || 0}
                  </p>
                </div>
                <div>
                  <p className='text-xs text-muted-foreground mb-1'>
                    Completed:
                  </p>
                  <p className='text-lg font-bold text-green-600 dark:text-green-400'>
                    {pkg.stats.completedBookings || 0}
                  </p>
                </div>
                <div>
                  <p className='text-xs text-muted-foreground mb-1'>
                    Cancelled:
                  </p>
                  <p className='text-lg font-bold text-red-600 dark:text-red-400'>
                    {pkg.stats.cancelledBookings || 0}
                  </p>
                </div>
              </div>
            </div>
          )} */}

          {/* Ratings */}
          {/* {pkg.ratings && (
            <div className='bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-4'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center space-x-2'>
                  <Star
                    size={16}
                    className='text-yellow-600 dark:text-yellow-400'
                  />
                  <span className='text-xs font-medium text-muted-foreground uppercase'>
                    Ratings
                  </span>
                </div>
                <div className='text-right'>
                  <p className='text-2xl font-bold text-yellow-600 dark:text-yellow-400'>
                    {pkg.ratings.averageRating?.toFixed(1) || '0.0'}
                  </p>
                  <p className='text-xs text-muted-foreground'>
                    {pkg.ratings.totalRatings || 0} ratings
                  </p>
                </div>
              </div>
            </div>
          )} */}

          {/* Timestamps */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border'>
            <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                <Calendar size={14} />
                <span>Created At</span>
              </label>
              <p className='text-sm'>{formatDateTime(pkg.createdAt)}</p>
            </div>

            <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                <Calendar size={14} />
                <span>Updated At</span>
              </label>
              <p className='text-sm'>{formatDateTime(pkg.updatedAt)}</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className='flex justify-end px-6 py-4 border-t border-border'>
          <button
            onClick={() => setIsViewModalOpen(false)}
            className='px-4 py-2 text-sm border border-border rounded-md hover:bg-accent transition-colors'
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
